import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { compact, dayShort, inr, num } from '../lib/format.js';

/*
  Hand-built SVG charts. The palette is the validated data-viz set (series 1–3 plus
  the fixed status colours), marks follow the house specs — 2px lines, ≤24px bars
  with a 4px rounded data end, 2px surface gaps and rings, hairline grid — and every
  chart carries a hover/focus tooltip and a table view, so no value is reachable
  only by pointing at it.
*/

const S1 = 'var(--series-1)';
const S2 = 'var(--series-2)';
const S3 = 'var(--series-3)';
export const SERIES = [S1, S2, S3];

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(640);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(240, entry.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Rounds the data end of a bar only; the baseline end stays square. */
function barPath(x, y, w, h, r, dir = 'right') {
  const rr = Math.max(0, Math.min(r, dir === 'right' ? w : h));
  if (dir === 'right') {
    return `M${x},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h - rr} Q${x + w},${y + h} ${x + w - rr},${y + h} H${x} Z`;
  }
  return `M${x},${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} H${x} Z`;
}

/** Labels never overflow their gutter; the full name stays in the tooltip. */
const truncate = (text, max) => (String(text).length > max ? `${String(text).slice(0, Math.max(3, max - 1))}…` : String(text));

function niceCeil(value) {
  if (value <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(value));
  const n = value / mag;
  // Tight steps, so the top gridline sits just above the data rather than at a
  // round number twice its height.
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((s) => n <= s) || 10;
  return step * mag;
}

function Tooltip({ at, children }) {
  if (!at) return null;
  return (
    <div className="tooltip" style={{ left: at.left, top: at.top, transform: at.flip ? 'translate(-108%, -50%)' : 'translate(12px, -50%)' }}>
      {children}
    </div>
  );
}

function DataToggle({ open, onToggle }) {
  return (
    <button type="button" className="data-toggle" onClick={onToggle} aria-expanded={open}>
      {open ? 'Hide data table' : 'Show data table'}
    </button>
  );
}

/* ── trend: one series over time ─────────────────────────────────────────── */
export function TrendArea({ data, valueKey = 'total', height = 230, unit = 'currency', caption }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const [table, setTable] = useState(false);
  const pad = { top: 14, right: 46, bottom: 26, left: 52 };
  const w = Math.max(280, width);
  const innerW = w - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const values = data.map((d) => num(d[valueKey]));
  const max = niceCeil(Math.max(1, ...values));
  const xAt = (i) => pad.left + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const yAt = (v) => pad.top + innerH - (num(v) / max) * innerH;
  const fmt = (v) => (unit === 'currency' ? inr(v, 0) : compact(v));

  const line = data.map((d, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(d[valueKey]).toFixed(1)}`).join(' ');
  const area = `${line} L${xAt(data.length - 1).toFixed(1)},${pad.top + innerH} L${xAt(0).toFixed(1)},${pad.top + innerH} Z`;
  const ticks = [0, 0.5, 1].map((t) => max * t);
  const last = data[data.length - 1];

  const onMove = useCallback((e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - box.left;
    const i = Math.max(0, Math.min(data.length - 1, Math.round(((x - pad.left) / innerW) * (data.length - 1))));
    setHover({ i, left: xAt(i), top: yAt(data[i][valueKey]), flip: xAt(i) > w - 150 });
  }, [data, innerW, w]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${w} ${height}`} height={height} role="img"
        aria-label={`${caption || 'Trend'}: ${data.length} days, latest ${fmt(last ? last[valueKey] : 0)}`}
        onPointerMove={onMove} onPointerLeave={() => setHover(null)}
        tabIndex={0} onFocus={() => setHover({ i: data.length - 1, left: xAt(data.length - 1), top: yAt(values[values.length - 1]), flip: true })}
        onKeyDown={(e) => {
          if (!hover) return;
          const next = e.key === 'ArrowLeft' ? hover.i - 1 : e.key === 'ArrowRight' ? hover.i + 1 : null;
          if (next === null) return;
          e.preventDefault();
          const i = Math.max(0, Math.min(data.length - 1, next));
          setHover({ i, left: xAt(i), top: yAt(data[i][valueKey]), flip: xAt(i) > w - 150 });
        }}>
        <defs>
          <linearGradient id="trendFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={S1} stopOpacity="0.16" />
            <stop offset="100%" stopColor={S1} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={w - pad.right} y1={yAt(t)} y2={yAt(t)} stroke="var(--grid)" strokeWidth="1" />
            <text x={pad.left - 10} y={yAt(t) + 4} textAnchor="end" className="num"
              fontSize="11" fill="var(--ink-muted)">{unit === 'currency' ? compact(t) : compact(t)}</text>
          </g>
        ))}
        <path d={area} fill="url(#trendFill)" />
        <path d={line} fill="none" stroke={S1} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => (i % Math.max(1, Math.ceil(data.length / Math.max(3, Math.floor(innerW / 96)))) === 0 && i < data.length - 2 ? (
          <text key={i} x={xAt(i)} y={height - 8} textAnchor="middle" fontSize="11" fill="var(--ink-muted)">{dayShort(d.date)}</text>
        ) : null))}
        {last && (
          <>
            <circle cx={xAt(data.length - 1)} cy={yAt(last[valueKey])} r="4.5" fill={S1} stroke="var(--chart-surface)" strokeWidth="2" />
            <text x={xAt(data.length - 1) + 10} y={yAt(last[valueKey]) + 4} fontSize="12" fontWeight="600" fill="var(--ink)">
              {unit === 'currency' ? compact(last[valueKey]) : compact(last[valueKey])}
            </text>
          </>
        )}
        {hover && (
          <>
            <line x1={hover.left} x2={hover.left} y1={pad.top} y2={pad.top + innerH} stroke="var(--axis)" strokeWidth="1" />
            <circle cx={hover.left} cy={hover.top} r="5" fill={S1} stroke="var(--chart-surface)" strokeWidth="2" />
          </>
        )}
      </svg>
      <Tooltip at={hover}>
        {hover && (
          <>
            <div className="tt-head">{dayShort(data[hover.i].date)}</div>
            <div className="tt-row">
              <span className="k"><i className="line-key" style={{ background: S1 }} />Billed</span>
              <span className="v">{fmt(data[hover.i][valueKey])}</span>
            </div>
            {data[hover.i].invoices !== undefined && (
              <div className="tt-row">
                <span className="k">Invoices</span>
                <span className="v">{data[hover.i].invoices}</span>
              </div>
            )}
          </>
        )}
      </Tooltip>
      <div style={{ marginTop: 8 }}><DataToggle open={table} onToggle={() => setTable(!table)} /></div>
      {table && (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table>
            <thead><tr><th>Date</th><th className="r">Invoices</th><th className="r">Billed</th></tr></thead>
            <tbody>
              {/* Every point on the chart gets a row — including the zero days. */}
              {data.slice().reverse().map((d) => (
                <tr key={d.date}><td>{dayShort(d.date)}</td><td className="r num">{num(d.invoices)}</td><td className="r num">{fmt(d[valueKey])}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── horizontal bars: comparison by magnitude ────────────────────────────── */
export function BarsH({ data, unit = 'currency', barHeight = 22, onSelect }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const labelW = Math.min(180, Math.max(96, width * 0.3));
  const valueW = 74;
  const trackW = Math.max(60, width - labelW - valueW - 8);
  const max = Math.max(1, ...data.map((d) => num(d.value)));
  const gap = 14;
  const height = data.length * (barHeight + gap);
  const fmt = (v) => (unit === 'currency' ? inr(v, 0) : compact(v));

  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${Math.max(280, width)} ${height}`} height={height} role="img"
        aria-label={`Ranked bars: ${data.map((d) => `${d.label} ${fmt(d.value)}`).join('; ')}`}>
        {data.map((d, i) => {
          const y = i * (barHeight + gap);
          const w = Math.max(3, (num(d.value) / max) * trackW);
          const active = hover && hover.i === i;
          return (
            <g key={d.label} tabIndex={0} role="button"
              onFocus={() => setHover({ i, left: labelW + w, top: y + barHeight / 2 })}
              onBlur={() => setHover(null)}
              onPointerEnter={() => setHover({ i, left: labelW + w, top: y + barHeight / 2 })}
              onPointerLeave={() => setHover(null)}
              onClick={() => onSelect && onSelect(d)}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}>
              <rect x="0" y={y - 4} width="100%" height={barHeight + 8} fill="transparent" />
              <text x="0" y={y + barHeight / 2 + 4} fontSize="12.5" fill="var(--ink-2)">
                {truncate(d.label, Math.floor(labelW / 7))}
              </text>
              <rect x={labelW} y={y} width={trackW} height={barHeight} rx="5" fill="var(--surface-sunk)" />
              <path d={barPath(labelW, y, w, barHeight, 4)} fill={SERIES[i % 3]} opacity={active ? 1 : 0.92} />
              <text x={labelW + w + 9} y={y + barHeight / 2 + 4} fontSize="12.5" fontWeight="600"
                className="num" fill="var(--ink)">{unit === 'currency' ? compact(d.value) : compact(d.value)}</text>
            </g>
          );
        })}
      </svg>
      <Tooltip at={hover && { ...hover, flip: hover.left > width - 160 }}>
        {hover && (
          <>
            <div className="tt-head">{data[hover.i].label}</div>
            <div className="tt-row">
              <span className="k"><i className="line-key" style={{ background: SERIES[hover.i % 3] }} />Value</span>
              <span className="v">{fmt(data[hover.i].value)}</span>
            </div>
            {data[hover.i].sub && <div className="tt-row"><span className="k">Quantity</span><span className="v">{data[hover.i].sub}</span></div>}
          </>
        )}
      </Tooltip>
    </div>
  );
}

/* ── stacked bars: two-part composition per category ─────────────────────── */
export function StackedBars({ data, keys, unit = 'qty', onSelect }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const labelW = Math.min(160, Math.max(92, width * 0.28));
  const trackW = Math.max(60, width - labelW - 78);
  const barHeight = 22;
  const gap = 18;
  const height = data.length * (barHeight + gap);
  const max = Math.max(1, ...data.map((d) => keys.reduce((a, k) => a + num(d[k.key]), 0)));
  const fmt = (v) => (unit === 'currency' ? inr(v, 0) : compact(v));

  return (
    <div className="chart" ref={ref}>
      <div className="chart-legend">
        {keys.map((k, i) => (
          <span className="key" key={k.key}>
            <i className="swatch" style={{ background: SERIES[i % 3] }} />{k.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${Math.max(280, width)} ${height}`} height={height} role="img"
        aria-label={`Stacked bars by ${keys.map((k) => k.label).join(' and ')}`}>
        {data.map((d, i) => {
          const y = i * (barHeight + gap);
          const total = keys.reduce((a, k) => a + num(d[k.key]), 0);
          let x = labelW;
          return (
            <g key={d.label} tabIndex={0} role="button"
              onFocus={() => setHover({ i, left: labelW + trackW / 2, top: y + barHeight / 2 })}
              onBlur={() => setHover(null)}
              onPointerEnter={() => setHover({ i, left: labelW + trackW / 2, top: y + barHeight / 2 })}
              onPointerLeave={() => setHover(null)}
              onClick={() => onSelect && onSelect(d)}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}>
              <text x="0" y={y + barHeight / 2 + 4} fontSize="12.5" fill="var(--ink-2)">{truncate(d.label, Math.floor(labelW / 7))}</text>
              <rect x={labelW} y={y} width={trackW} height={barHeight} rx="5" fill="var(--surface-sunk)" />
              {keys.map((k, ki) => {
                const value = num(d[k.key]);
                const w = total ? (value / max) * trackW : 0;
                const seg = w > 2 ? (
                  <path key={k.key} d={barPath(x, y, Math.max(2, w - (ki < keys.length - 1 ? 2 : 0)), barHeight, ki === keys.length - 1 ? 4 : 0)}
                    fill={SERIES[ki % 3]} />
                ) : null;
                x += w;
                return seg;
              })}
              <text x={labelW + trackW + 10} y={y + barHeight / 2 + 4} fontSize="12.5" fontWeight="600"
                className="num" fill="var(--ink)">{compact(total)}</text>
            </g>
          );
        })}
      </svg>
      <Tooltip at={hover && { ...hover, flip: hover.left > width - 170 }}>
        {hover && (
          <>
            <div className="tt-head">{data[hover.i].label}</div>
            {keys.map((k, ki) => (
              <div className="tt-row" key={k.key}>
                <span className="k"><i className="line-key" style={{ background: SERIES[ki % 3] }} />{k.label}</span>
                <span className="v">{fmt(data[hover.i][k.key])}</span>
              </div>
            ))}
          </>
        )}
      </Tooltip>
    </div>
  );
}

/* ── small columns: sync activity, good vs failed ────────────────────────── */
export function ActivityBars({ data, height = 84 }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const w = Math.max(240, width);
  const slot = w / Math.max(1, data.length);
  const barW = Math.min(18, slot - 6);
  const max = Math.max(1, ...data.map((d) => num(d.done) + num(d.failed)));

  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${w} ${height}`} height={height} role="img"
        aria-label={`Sync jobs over ${data.length} days`}>
        {data.map((d, i) => {
          const done = num(d.done);
          const failed = num(d.failed);
          const x = i * slot + (slot - barW) / 2;
          const hDone = (done / max) * (height - 20);
          const hFail = (failed / max) * (height - 20);
          const yFail = height - 16 - hFail;
          const yDone = yFail - hDone - (failed && done ? 2 : 0);
          return (
            <g key={d.date} tabIndex={0} role="button"
              onFocus={() => setHover({ i, left: x + barW / 2, top: yDone })}
              onBlur={() => setHover(null)}
              onPointerEnter={() => setHover({ i, left: x + barW / 2, top: yDone })}
              onPointerLeave={() => setHover(null)}>
              <rect x={x - 3} y="0" width={barW + 6} height={height} fill="transparent" />
              {failed > 0 && <path d={barPath(x, yFail, barW, hFail, 0, 'up')} fill="var(--critical)" />}
              {done > 0 && <path d={barPath(x, yDone, barW, Math.max(2, hDone), 4, 'up')} fill="var(--series-3)" />}
              {done === 0 && failed === 0 && <rect x={x} y={height - 18} width={barW} height="2" rx="1" fill="var(--grid)" />}
            </g>
          );
        })}
        <line x1="0" x2={w} y1={height - 15} y2={height - 15} stroke="var(--axis)" strokeWidth="1" />
      </svg>
      <Tooltip at={hover && { ...hover, flip: hover.left > width - 150 }}>
        {hover && (
          <>
            <div className="tt-head">{dayShort(data[hover.i].date)}</div>
            <div className="tt-row"><span className="k"><i className="line-key" style={{ background: S3 }} />Completed</span><span className="v">{data[hover.i].done}</span></div>
            <div className="tt-row"><span className="k"><i className="line-key" style={{ background: 'var(--critical)' }} />Failed</span><span className="v">{data[hover.i].failed}</span></div>
          </>
        )}
      </Tooltip>
      <div className="chart-legend" style={{ marginTop: 8, marginBottom: 0 }}>
        <span className="key"><i className="swatch" style={{ background: S3 }} />Completed</span>
        <span className="key"><i className="swatch" style={{ background: 'var(--critical)' }} />Failed</span>
      </div>
    </div>
  );
}

/** A two-state meter — readiness, coverage, anything with a "done" share. */
export function Meter({ parts, caption }) {
  const total = parts.reduce((a, p) => a + num(p.value), 0) || 1;
  return (
    <div>
      <div className="meter" role="img" aria-label={parts.map((p) => `${p.label} ${p.value}`).join(', ')}>
        {parts.map((p, i) => (
          <span key={p.label} style={{
            width: `${(num(p.value) / total) * 100}%`,
            background: p.color || SERIES[i % 3],
            marginRight: i < parts.length - 1 ? 2 : 0,
          }} />
        ))}
      </div>
      <div className="chart-legend" style={{ marginTop: 10, marginBottom: 0 }}>
        {parts.map((p, i) => (
          <span className="key" key={p.label}>
            <i className="swatch" style={{ background: p.color || SERIES[i % 3] }} />
            {p.label} <b className="num" style={{ marginLeft: 2 }}>{p.value}</b>
          </span>
        ))}
      </div>
      {caption && <div className="card-note" style={{ marginTop: 8 }}>{caption}</div>}
    </div>
  );
}

/** 12-point sparkline for a stat tile: history recessive, the latest point accented. */
export function Sparkline({ values, width = 92, height = 28 }) {
  const vals = values.slice(-12).map(num);
  if (vals.length < 2) return null;
  const max = Math.max(...vals, 1);
  const x = (i) => (i / (vals.length - 1)) * width;
  const y = (v) => height - 2 - (v / max) * (height - 6);
  const d = vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} aria-hidden="true" style={{ display: 'block' }}>
      <path d={d} fill="none" stroke="var(--ink-muted)" strokeOpacity="0.45" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx={x(vals.length - 1)} cy={y(vals[vals.length - 1])} r="3" fill={S1} stroke="var(--surface)" strokeWidth="2" />
    </svg>
  );
}

export function useKeyboardShortcut(combo, handler) {
  useEffect(() => {
    const onKey = (e) => {
      const isCombo = combo.key === e.key.toLowerCase() && (!combo.meta || e.metaKey || e.ctrlKey);
      if (isCombo) { e.preventDefault(); handler(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo.key, combo.meta, handler]);
}

